import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { ChevronRight } from "lucide-react"
import { bidSectionPath } from "@/lib/bid-sections"
import { ResponsesSection } from "@/components/ResponsesSection"
import { apiRequest, type BidArtifact } from "@/lib/api"
import {
  summariseSectionProgress,
  type SectionAssignment,
  type SectionProgress,
} from "@/components/overview/ResponseProgress"

function useSectionProgress(artifact: BidArtifact): SectionProgress | null {
  const sections = artifact.outlineJson?.sections
  const [progress, setProgress] = useState<SectionProgress | null>(null)

  useEffect(() => {
    if (!sections || sections.length === 0) return
    let cancelled = false
    apiRequest<{ assignments: SectionAssignment[] }>(`/artifacts/${artifact.id}/assignments`)
      .then(({ assignments }) => {
        if (!cancelled) setProgress(summariseSectionProgress(sections, assignments))
      })
      .catch(() => {
        // Supplementary: the row still opens the document.
        if (!cancelled) setProgress(summariseSectionProgress(sections, []))
      })
    return () => {
      cancelled = true
    }
  }, [artifact.id, sections])

  return progress
}

function progressLine(artifact: BidArtifact, progress: SectionProgress | null): string {
  if (artifact.artifactType === "excel_schedule") return "Schedule"
  // A form's filled count needs its field model (one S3 read per row), so the
  // list shows none rather than repeat the Type column; the form page has it.
  if (artifact.artifactType === "word_form") return "—"
  if (artifact.scopeKind === "artifact") return "Single document"
  const total = artifact.outlineJson?.sections.length ?? 0
  const noun = artifact.artifactType === "questionnaire" ? "question" : "section"
  if (total === 0) return `No ${noun}s yet`
  const count = `${total} ${noun}${total === 1 ? "" : "s"}`
  if (!progress) return count
  return progress.attention > 0 ? `${count} · ${progress.attention} need attention` : `${count} · all under way`
}

function WriteProgress({ artifact }: { artifact: BidArtifact }) {
  const progress = useSectionProgress(artifact)
  const percent =
    progress && progress.total > 0 ? ((progress.total - progress.attention) / progress.total) * 100 : null

  return (
    <div className="space-y-1">
      <p className="text-meta">{progressLine(artifact, progress)}</p>
      {percent !== null && (
        <div
          role="progressbar"
          aria-label={`${artifact.name} progress`}
          aria-valuenow={Math.round(percent)}
          aria-valuemin={0}
          aria-valuemax={100}
          className="h-1.5 w-28 shrink-0 overflow-hidden rounded-full bg-muted"
        >
          <div className="h-full rounded-full bg-foreground/70" style={{ width: `${percent}%` }} />
        </div>
      )}
    </div>
  )
}

/**
 * Write: the existing document setup/list, with the same attention counts
 * and progress previously shown by the compact Write list.
 */
export function WriteSection({ bidId }: { bidId: string }) {
  return (
    <section aria-label="Response documents" className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-medium">Response documents</h2>
        <Link
          to={bidSectionPath(bidId, "qualify", "submission_checklist")}
          className="inline-flex items-center gap-0.5 rounded text-sm text-[hsl(var(--link))] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          Submission checklist
          <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
        </Link>
      </div>
      <ResponsesSection bidId={bidId} renderProgress={renderProgress} />
    </section>
  )
}

function renderProgress(artifact: BidArtifact) {
  return <WriteProgress artifact={artifact} />
}
